import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../services/toast/toast.service';

/**
 * Persistent (non-blocking) banner that appears once the session has less than
 * WARNING_THRESHOLD_MS left, showing a live MM:SS countdown and a "Stay signed in" action that
 * triggers AuthService.manualRefresh() on demand — on top of the existing silent background
 * refresh, so the user isn't left guessing whether it's about to happen.
 */
@Component({
  selector: 'app-session-expiry-banner',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './session-expiry-banner.component.html',
  styleUrls: ['./session-expiry-banner.component.scss'],
})
export class SessionExpiryBannerComponent implements OnInit, OnDestroy {

  /** Mirrors AuthService's own silent-refresh buffer, so the banner appears right as that would kick in. */
  private static readonly WARNING_THRESHOLD_MS = 2 * 60 * 1000;

  /**
   * Unauthenticated / public route prefixes the banner must NEVER render on — a session-expiry
   * warning is meaningless on sign-in, sign-up, forgot-password, etc. Matched against router.url.
   */
  private static readonly PUBLIC_ROUTE_PREFIXES = [
    '/welcome',
    '/language-setup',
    '/signIn',
    '/signup',
    '/password-reset',
    '/transactionVerification',
  ];

  visible = false;
  isRefreshing = false;
  countdownLabel = '';

  /** Set when the user dismisses the banner; keeps the 1s ticker from immediately re-showing it. Reset on the next warning cycle. */
  private dismissed = false;
  /** Last value from sessionRemainingMs$, so a route change can re-evaluate visibility without waiting for the next tick. */
  private lastRemainingMs: number | null = null;

  private subscription?: Subscription;

  constructor(
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly ngZone: NgZone,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.subscription = this.authService.sessionRemainingMs$.subscribe((remainingMs) => {
      this.ngZone.run(() => this.handleRemaining(remainingMs));
    });

    // Re-evaluate on navigation so the banner disappears the instant we land on a public page.
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.ngZone.run(() => this.handleRemaining(this.lastRemainingMs))),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /** Hide the banner on user request; the silent background refresh keeps running regardless. */
  dismiss(): void {
    this.dismissed = true;
    this.visible = false;
  }

  async extendSession(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    const success = await this.authService.manualRefresh();
    this.isRefreshing = false;

    if (success) {
      this.visible = false;
      await this.toastService.presentToast('Session extended', "You're good to continue.", 'bottom', 'success', 2500);
    } else {
      await this.toastService.presentToast(
        'Could not extend session',
        'Please try again — you may be signed out soon.',
        'bottom', 'danger', 3500
      );
    }
  }

  private handleRemaining(remainingMs: number | null): void {
    this.lastRemainingMs = remainingMs;

    if (remainingMs === null || remainingMs <= 0 || remainingMs > SessionExpiryBannerComponent.WARNING_THRESHOLD_MS) {
      this.visible = false;
      this.dismissed = false; // fresh warning cycle (e.g. after a successful refresh) — allow it to show again
      return;
    }

    // Never render on unauthenticated pages, and stay hidden once the user has dismissed it.
    if (this.isPublicRoute() || this.dismissed) {
      this.visible = false;
      return;
    }

    this.visible = true;
    this.countdownLabel = this.formatCountdown(remainingMs);
  }

  private isPublicRoute(): boolean {
    const path = (this.router.url || '').split(/[?#]/)[0].toLowerCase();
    return SessionExpiryBannerComponent.PUBLIC_ROUTE_PREFIXES.some((prefix) => {
      const p = prefix.toLowerCase();
      return path === p || path.startsWith(`${p}/`);
    });
  }

  private formatCountdown(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
